import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { blogPosts, pillarCategories } from '../../mocks/blog-posts';
import { useSEO } from '../../utils/seo';

export default function Blog() {
  const [selectedPillar, setSelectedPillar] = useState<string>('All');

  // SEO Configuration
  useSEO({
    title: 'Blog - Family Micro-Habits, Parenting Tips & Intentional Living',
    description: 'Practical guidance, inspiration, and micro-habit ideas to help your family grow together. Explore articles on Faith, Family, Friends, Fitness, Finances, Food, and Fun.',
    keywords: 'family blog, parenting tips, micro-habits blog, intentional living, family growth articles, faith-based parenting, family fitness tips',
    canonical: '/blog',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Rooted & Rising Blog',
      description: 'Practical guidance, inspiration, and micro-habit ideas to help your family grow together',
      url: `${import.meta.env.VITE_SITE_URL || ''}/blog`,
      publisher: {
        '@type': 'Organization',
        name: 'Rooted & Rising',
        url: import.meta.env.VITE_SITE_URL || ''
      },
      blogPost: blogPosts.slice(0, 5).map(post => ({
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        datePublished: new Date(post.date).toISOString()
      }))
    }
  });

  const filteredPosts = selectedPillar === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.pillar === selectedPillar);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-amber-50 to-rose-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            The Blog
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Practical guidance, inspiration, and micro-habit ideas to help your family grow together
          </p>
        </div>
      </section>

      {/* Filter Pills */}
      <section className="py-12 px-4 border-b border-gray-200">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setSelectedPillar('All')}
              className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                selectedPillar === 'All'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Posts
            </button>
            {pillarCategories.map((pillar) => (
              <button
                key={pillar.name}
                onClick={() => setSelectedPillar(pillar.name)}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedPillar === pillar.name
                    ? pillar.color
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {pillar.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article 
                key={post.id}
                className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden hover:border-amber-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="relative h-56 w-full">
                  <img 
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <span className={`inline-block px-3 py-1 ${getPillarColor(post.pillar)} rounded-full text-xs font-medium mb-3`}>
                    {post.pillar}
                  </span>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{post.date}</span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-file-list-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-500">No posts found in this category</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 px-4 bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Never Miss a Post
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Get weekly micro-habit ideas and family guidance delivered to your inbox
          </p>
          <Link 
            to="/" 
            className="inline-flex items-center px-8 py-4 bg-amber-500 text-white rounded-full font-medium hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Subscribe to Newsletter
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function getPillarColor(pillar: string): string {
  const colors: Record<string, string> = {
    'Faith': 'bg-amber-100 text-amber-800',
    'Family': 'bg-rose-100 text-rose-800',
    'Friends': 'bg-emerald-100 text-emerald-800',
    'Fitness': 'bg-cyan-100 text-cyan-800',
    'Finances': 'bg-violet-100 text-violet-800',
    'Food': 'bg-orange-100 text-orange-800',
    'Fun': 'bg-pink-100 text-pink-800'
  };
  return colors[pillar] || 'bg-gray-100 text-gray-800';
}